import React from "react";
import { stripHtmlTags, takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { useFragment, type FragmentType } from "~/gql/runtime";
import { EditorialCard } from "~/features/card/editorial-card";
import { EditorialFeaturedCard } from "~/features/card/editorial-featured-card";
import { graphql } from "~/gql/runtime";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";

const fragment = graphql(`
  fragment WebinarCard on webinar_default_Entry {
    id
    headline: title
    linkText: baseText
    url
    hero {
      ... on hero_simpleHero_BlockType {
        text: baseRichText
        media: baseMedia {
          ...Media
        }
      }
    }
  }
`);

export type WebinarCardViewProps = {
  featured?: boolean;
  size?: "regular" | "large";
  headline?: string | null;
  text?: string | null;
  url?: string | null;
  linkText?: string | null;
  media?: React.ReactNode;
};

export function WebinarCardView(props: WebinarCardViewProps) {
  const { featured, size = "regular", headline, text, url, linkText, media } = props;

  const Card = featured ? EditorialFeaturedCard : EditorialCard;

  return (
    <Card
      headline={headline ?? undefined}
      text={stripHtmlTags(text ?? "")}
      url={url ?? undefined}
      linkText={linkText ?? "Watch Now"}
      size={size}
      media={media}
    />
  );
}

type Props = FragmentType<typeof fragment> & {
  featured?: boolean;
  size?: "regular" | "large";
};

export function WebinarCard(props: Props) {
  const { featured, size = "regular" } = props;
  const { headline, hero, url, linkText } = useFragment(fragment, props);
  const { text, media } = takeFirst(hero) || {};
  const isLg = useMediaQuery(up("lg"));

  const ratio = isLg ? "496/280" : "310/260";

  return (
    <WebinarCardView
      featured={featured}
      size={size}
      headline={headline}
      text={text}
      url={url}
      linkText={linkText}
      media={
        <Media
          {...takeFirst(media)}
          imageProps={{ ratio, sizes: "lg:50vw" }}
          externalVideoProps={{ ratio }}
          inlineVideoProps={{ ratio }}
        />
      }
    />
  );
}
